/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type HttpResponse, EventService, HttpService } from '@dm/core';
import { FeatureConfig } from 'app/config/feature';
import { SWITCH } from 'app/config/switch';
import { type Subscriber, Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { GlobalDataService } from './global-data.service';

@Injectable({
    providedIn: 'root',
})
export class SmartGuiService {
    /**
     * 智能GUI开关状态
     */
    switchStatus = false;

    get switch(): boolean {
        return !FeatureConfig.IsHideSmartGUIWithoutAlarmInDM ? this.switchStatus : false;
    }

    get switchOceanProtect(): boolean {
        return FeatureConfig.IsHideSmartGUIWithoutAlarmInDM ? this.switchStatus : false;
    }

    constructor(
        private httpService: HttpService,
        private globalDataService: GlobalDataService,
        public eventService: EventService,
    ) { }

    /**
     * 初始化
     */
    init(): Observable<void> {
        return Observable.create((observer: Subscriber<void>) => {
            this.globalDataService.getSystemInfoSync().pipe(finalize(() => {
                observer.next();
                observer.complete();
            })).subscribe(() => {
                const isSupport = SWITCH.SUPPORT_SMART_GUI &&
                    !SWITCH.IS_VSTORE_SYSTEM &&
                    !FeatureConfig.IsHideSmartGui &&
                    SWITCH.PRODUCT_SUPPORT_AI;
                if (isSupport) {
                    this.getSwitchState();
                }
            });
        });
    }

    /**
     * 获取开关状态
     */
    getSwitchState(): void {
        this.querySwitchState().subscribe(({ data }) => {
            if (data) {
                this.switchStatus = data.status === '1' ? true : false;
            }
        });
    }

    /**
     * 切换开关状态
     */
    toggleSwitch = (): void => {
        const status = this.switchStatus ? '0' : '1';
        this.modifySwitchState(status).subscribe(() => {
            this.switchStatus = status === '1' ? true : false;
            // 调用getSmartfixalarm来获取告警列表
            if (this.switchStatus) {
                this.eventService.emit('gui-switch-changed');
            }
        });
    };

    querySwitchState(): Observable<HttpResponse> {
        return this.httpService.get(this.httpService.makeUrl('smartgui.querySwitchState', 'V2'));
    }

    modifySwitchState(status: string): Observable<HttpResponse> {
        return this.httpService.put(this.httpService.makeUrl('smartgui.modifySwitchState', 'V2'), { status }, { mask: true });
    }
}
